import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { DynamicFormRepository } from '@/store/dynamic-form-state/dynamic-form.repository';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { DynamicFieldsService } from '../dynamic-fields.service';
import { DynamicFormsService } from '../dynamic-forms.service';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { DynamicFormsWorkflowBlocksService } from '../dynamic-forms-workflow-blocks.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { dynamicFieldStoreFactory } from '@/store/dynamic-form-state/dynamic-field-state/dynamic-field.store';
import { DynamicForm as Fb, SelectOption } from '@/components/form-builder/form-builder.types';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { getEditDynamicFormFormObject } from './utils/dynamic-form.form-object';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { DynamicFormDto, DynamicForm } from '@/types';
import { dynamicFormSchema } from '@/validations/dynamic-form.validation';
import { DocSubmissionService } from '../../doc-submission/doc-submission.service';

@Component({
  selector: 'sm-edit-dynamic-form',
  imports: [PageHeaderComponent, FormBuilderComponent, BlockBuilderComponent, SpinnerComponent],
  templateUrl: './edit-dynamic-form.component.html',
  styleUrl: './edit-dynamic-form.component.scss',
})
export class EditDynamicFormComponent implements OnInit, OnDestroy {
  dynmicFormRepository = inject(DynamicFormRepository);
  docSubmissionService = inject(DocSubmissionService);
  multiFormAccordionService = inject(MultiFormAccordionService);
  dynamicFieldsService = inject(DynamicFieldsService);
  dynamicFormService = inject(DynamicFormsService);
  messageService = inject(MessageService);
  router = inject(Router);
  translate = inject(TranslateService);
  activatedRoute = inject(ActivatedRoute);

  id = this.activatedRoute.snapshot.params['id'] as number;

  dynamicFormWorkflowBlocksService = inject(DynamicFormsWorkflowBlocksService);
  controlBlocks: DynamicBlocks = this.dynamicFormWorkflowBlocksService.getPreDraftBlocks({
    save: () => {
      this.updateDynamicForm();
    },
    rest: () => {
      const data = this.dynmicFormRepository.get<DynamicForm>('response');
      this.dynmicFormRepository.setDynamicFormDto(data);
      this.dynamicFieldsService.setDynamicFieldDtos(data.fields);
    },
  });

  breadcrumb = [
    {
      label: 'transanctionalForm.title',
      routerLink: ['/settings', 'dynamic-forms'],
    },
    {
      label: 'transanctionalForm.pages.edit',
      routerLink: ['/settings', 'dynamic-forms-edit', this.id.toString()],
    },
  ];

  loading$ = new BehaviorSubject<boolean>(false);
  title: string = this.translate.instant('transanctionalForm.pages.edit');

  folderTypeOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>([]);
  formObject: Fb = getFormBuilderObjectFactory();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
    this.dynmicFormRepository.reset();
  }

  loadData() {
    this.loading$.next(true);
    this.multiFormAccordionService.formFactory = (id: string) => dynamicFieldStoreFactory(id);
    this.dynamicFieldsService.initialize();

    this.docSubmissionService.getAllFolderTypes().subscribe({
      next: data => {
        this.folderTypeOptions.next(
          data.map(typeDossier => ({
            name: typeDossier.libelle,
            code: typeDossier.id,
          }))
        );
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message,
        });
      },
    });

    this.dynamicFormService.getDynamicForm(this.id).subscribe({
      next: data => {
        this.dynmicFormRepository.set('response', data);
        this.dynmicFormRepository.setDynamicFormDto(data);
        this.dynamicFieldsService.setDynamicFieldDtos(data.fields);
        this.formObject = getEditDynamicFormFormObject({
          store: this.dynmicFormRepository,
          folderTypeOptions: this.folderTypeOptions,
          resetFormType: () => this.dynamicFieldsService.restFormType(),
        });
        this.loading$.next(false);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.message || 'Une erreur est survenue lors de la récupération du formulaire dynamique',
        });
        this.loading$.next(false);
      },
    });
  }

  updateDynamicForm() {
    const data = this.dynmicFormRepository.get<DynamicFormDto>('dto');
    const fieldsValidation = this.dynamicFieldsService.validateAllFieldsDtos();
    const result = dynamicFormSchema.safeParse(data);

    if (result.success && fieldsValidation) {
      data.fields = this.dynamicFieldsService.getAllDynamicFieldsDtos();
      this.loading$.next(true);
      this.dynamicFormService.updateDynamicForm(this.id, data).subscribe({
        next: () => {
          this.multiFormAccordionService.reset(0);
          this.dynmicFormRepository.reset();
          this.router.navigate(['/settings', 'dynamic-forms']);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Le formulaire dynamique a été mis à jour avec succès',
          });
          this.loading$.next(false);
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || 'Une erreur est survenue lors de la mise à jour du formulaire dynamique',
          });
          this.loading$.next(false);
        },
      });
    } else if (result.error) {
      this.dynmicFormRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }
}
